import React, { useState, useEffect } from "react";
import {
  Box, Drawer, List, Button, ListItem, ListItemIcon, ListItemText,
  Collapse, Typography, ListItemButton, Grid, CssBaseline, useMediaQuery,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import Logo from "../../assets/ar-infotech-solutions.png";
import SettingsIcon from "@mui/icons-material/Settings";
import DashboardIcon from "@mui/icons-material/Dashboard";
import LeadsIcon from "@mui/icons-material/BusinessCenter";
import EmployeeIcon from "@mui/icons-material/Person";
import BlogIcon from "@mui/icons-material/Description";
import LoginIcon from "@mui/icons-material/Login";
import { School as SchoolIcon, LibraryBooks as LibraryBooksIcon, Person as PersonIcon } from "@mui/icons-material";
import WorkIcon from "@mui/icons-material/Work";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import DoNotDisturbAltIcon from "@mui/icons-material/DoNotDisturbAlt";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import CachedIcon from "@mui/icons-material/Cached";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import PlusIcon from "@mui/icons-material/Add";
import ListIcon from "@mui/icons-material/List";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import AddIcon from "@mui/icons-material/Add";
import BookIcon from "@mui/icons-material/Book";
import { useCookies } from "react-cookie";
import { useDispatch, useSelector } from "react-redux";
import { config } from "../../config/config";
import { getUser } from "../../global/redux/action";
import { useDashboard } from "../../context/DashboardContext";
import MiniDrawer from "./IconSidenav";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import InventoryIcon from "@mui/icons-material/Inventory";
import CategoryIcon from "@mui/icons-material/Category";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import CustomersIcon from "@mui/icons-material/People";

import { useUser } from "../../context/UserContext";

const drawerWidth = 240;

const Sidebar = () => {
  const { isSidebarOpen, toggleSidebar } = useDashboard();
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(true);
  const [openSubList, setOpenSubList] = useState({});
  const isSmallScreen = useMediaQuery("(min-width:1024px) and (max-width:1440px)");
  const isMobile = useMediaQuery("(max-width:900px)");

  const [cookies] = useCookies();
  const globalState = useSelector((state) => state);
  const { userData, userError } = globalState.userReducer;
  const dispatch = useDispatch();

  useEffect(() => {
    menuItems.forEach((item, index) => {
      if (item.subItems.some((subItem) => subItem.path === location.pathname)) {
        setOpenSubList((prev) => ({ ...prev, [index]: true }));
      }
    });
  }, [location.pathname]);

  const handleSubListClick = (index) => {
    setOpenSubList((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const handleNavigation = (path, parentIndex) => {
    navigate(path);
    setOpenSubList((prev) => ({ ...prev, [parentIndex]: true }));
    if (isMobile) {
      toggleSidebar();
    }
  };

  useEffect(() => { setOpen(true); }, [location.pathname]);

  useEffect(() => {
    if (userError) console.warn("Notice: user details fetch error:", userError);
  }, [userError]);

  useEffect(() => {
    let loginUserId = null;
    const rawCookie = cookies[config.cookieName];
    if (rawCookie) {
      if (typeof rawCookie === 'object' && rawCookie.loginUserId) {
        loginUserId = rawCookie.loginUserId;
      } else if (typeof rawCookie === 'string') {
        try {
          loginUserId = JSON.parse(rawCookie).loginUserId;
        } catch (e) { }
      }
    }
    if (!loginUserId && user?.id) {
      loginUserId = user.id;
    }
    if (loginUserId && !userData?.users) {
      dispatch(getUser({ id: loginUserId, cookies }));
    }
  }, [dispatch, cookies, userData, user]);

  const rawRoles = userData?.users?.roles || user?.roles || user?.role || ["Super Admin"];
  const userRole = Array.isArray(rawRoles)
    ? rawRoles
    : typeof rawRoles === "string"
      ? rawRoles.split(",").map(r => r.trim())
      : ["Super Admin"];

  const getMenuItemsBasedOnRole = (userRoles) => {
    return [
      {
        text: "Dashboard",
        icon: <DashboardIcon />,
        path: "/dashboard",
        subItems: [],
      },
      {
        text: "Products",
        icon: <InventoryIcon />,
        subItems: [
          { text: "All Products", path: "/inventory-manager/product-list", icon: <ListIcon /> },
          { text: "Windows OS", path: "/inventory-manager/product-list?cat=windows", icon: <CategoryIcon /> },
          { text: "MS Office", path: "/inventory-manager/product-list?cat=ms-office", icon: <CategoryIcon /> },
          { text: "Servers", path: "/inventory-manager/product-list?cat=servers", icon: <CategoryIcon /> },
          { text: "Adobe Products", path: "/inventory-manager/product-list?cat=adobe", icon: <AutoAwesomeIcon /> },
          { text: "Others (Antivirus, Tally, etc.)", path: "/inventory-manager/product-list?cat=others", icon: <CategoryIcon /> },
          { text: "Combos", path: "/inventory-manager/product-list?cat=combo", icon: <CategoryIcon /> },
          { text: "Add New Product", path: "/inventory-manager/add-new-product", icon: <AddIcon /> },
          { text: "Manage Categories", path: "/inventory-manager/categories", icon: <CategoryIcon /> },
        ],
      },
      {
        text: "Orders",
        icon: <ShoppingBagIcon />,
        path: "/orders-manager/order-list",
        subItems: [],
      },
      {
        text: "Customers",
        icon: <CustomersIcon />,
        path: "/customer-manager/customers",
        subItems: [],
      },
      {
        text: "Category Manager",
        icon: <CategoryIcon />,
        path: "/inventory-manager/categories",
        subItems: [],
      },
      {
        text: "Tracking",
        icon: <LocalShippingIcon />,
        subItems: [
          { text: "Tracking List", path: "/tracking-manager/list", icon: <ListIcon /> },
          { text: "Add Tracking", path: "/tracking-manager/add", icon: <AddIcon /> },
        ],
      },
      {
        text: "Invoice",
        icon: <ReceiptLongIcon />,
        subItems: [
          { text: "Create Invoice", path: "/invoice-manager/create-invoice", icon: <AddIcon /> },
          { text: "Invoice List", path: "/invoice-manager/invoices", icon: <ListIcon /> },
        ],
      },
      {
        text: "Leads Manager",
        icon: <LeadsIcon />,
        subItems: [
          { text: "Fresh Leads", path: "/lead-manager/fresh", icon: <PlaylistAddCheckIcon /> },
          { text: "Fake Leads", path: "/lead-manager/fake", icon: <DoNotDisturbAltIcon /> },
          { text: "Follow-up Leads", path: "/lead-manager/follow-up", icon: <ThumbUpOffAltIcon /> },
          { text: "Converted Leads", path: "/lead-manager/converted", icon: <CachedIcon /> },
          { text: "Generate Leads", path: "/lead-manager/generate", icon: <AutoAwesomeIcon /> },
        ],
      },
      {
        text: "Employees Manager",
        icon: <EmployeeIcon />,
        subItems: [
          { text: "Users", path: "/employee-manager/users", icon: <PersonIcon /> },
          { text: "Add New User", path: "/employee-manager/add-user", icon: <PersonIcon /> },
        ],
      },
      // {
      //   text: "Blog",
      //   icon: <BlogIcon />,
      //   subItems: [
      //     { text: "Add Blog", path: "/blog-manager/add-blog", icon: <PlusIcon /> },
      //     { text: "Blog List", path: "/blog-manager/blog-list", icon: <ListIcon /> },
      //   ],
      // },
      // {
      //   text: "SEO",
      //   icon: <BlogIcon />,
      //   subItems: [
      //     { text: "Add Site", path: "/seo-manager/add-sites", icon: <AddIcon /> },
      //   ],
      // },
    ].filter((item) => {
      const normalizedRoles = (Array.isArray(userRoles) ? userRoles : [userRoles]).map(r => String(r).trim().toLowerCase());
      if (normalizedRoles.includes("super admin")) {
        return true;
      }
      if (normalizedRoles.includes("admin")) {
        return ["Dashboard", "Products", "Orders", "Customers", "Category Manager", "Tracking", "Invoice", "Leads Manager", "Employees Manager", "Blog"].includes(item.text);
      }
      if (normalizedRoles.includes("team leader") || normalizedRoles.includes("telecaller")) {
        return ["Dashboard", "Leads Manager"].includes(item.text);
      }
      if (normalizedRoles.includes("blog author")) {
        return ["Dashboard", "Blog"].includes(item.text);
      }
      if (normalizedRoles.includes("partner")) {
        return ["Dashboard", "Leads Manager"].includes(item.text);
      }
      if (normalizedRoles.includes("seodatapro")) {
        return ["Dashboard", "SEO"].includes(item.text);
      }
      return true; // Default fallback for staff accessing dashboard
    });
  };

  const menuItems = getMenuItemsBasedOnRole(userRole);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);

  const handleClick = (event, index) => { setAnchorEl(event.currentTarget); setSelectedIndex(index); };
  const handleClose = () => { setAnchorEl(null); setSelectedIndex(null); };
  const openPopper = Boolean(anchorEl);
  const id = openPopper ? "simple-popover" : undefined;

  const drawerContent = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Grid sx={{ display: "flex", alignItems: "center", justifyContent: "center", background: "rgb(24, 20, 20)", boxShadow: "0px 1px 2px rgba(0,0,0,0.4)", cursor: "pointer" }}>
        <Grid sx={{ my: 1, display: "flex", alignItems: "center", justifyContent: "center", width: isSmallScreen ? 120 : 180, height: isSmallScreen ? 90 : 120 }}>
          <img src={Logo} alt="logo" style={{ height: "100%", objectFit: "contain" }} />
        </Grid>
      </Grid>
      <Box
        sx={{
          overflowY: "auto",
          overflowX: "hidden",
          flexGrow: 1,
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#F1F5F9",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#CBD5E1",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#94A3B8",
          },
          scrollbarWidth: "thin",
          scrollbarColor: "#CBD5E1 #F1F5F9",
        }}
      >
        <List>
          {menuItems.map((item, index) => (
            <div key={index} style={{ px: 2 }}>
              <ListItem
                button
                onClick={() => item.path ? handleNavigation(item.path, index) : handleSubListClick(index)}
                sx={{
                  "&:hover": { background: "#f0f8ff" },
                  background: location.pathname === item.path ? "#f0f8ff" : "transparent",
                }}
              >
                <ListItemIcon sx={{ minWidth: 30 }}>
                  {React.cloneElement(item.icon, {
                    sx: { fontSize: isSmallScreen ? 26 : 28, color: location.pathname === item.path ? "gray" : "#1D1A57", py: "2px", px: 1 },
                  })}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography sx={{ color: location.pathname === item.path ? "gray" : "#1d1a57", fontSize: isSmallScreen ? 13 : 14, fontWeight: 600 }}>
                      {item.text}
                    </Typography>
                  }
                />
                {item.subItems.length > 0 ? (openSubList[index] ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />) : null}
              </ListItem>
              {item.subItems.length > 0 && (
                <Collapse in={openSubList[index]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.subItems.map((subItem, subIndex) => (
                      <ListItemButton
                        key={subIndex}
                        sx={{
                          pl: 4, py: "4px",
                          "&:hover": { background: "#f0f8ff" },
                          background: location.pathname === subItem.path ? "#f0f8ff" : "transparent",
                        }}
                        onClick={() => handleNavigation(subItem.path, index)}
                      >
                        <ListItemIcon sx={{ minWidth: 30 }}>
                          {subItem.icon && React.cloneElement(subItem.icon, {
                            sx: { fontSize: 22, color: location.pathname === subItem.path ? "gray" : "#1D1A57", px: 1 },
                          })}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography sx={{ color: location.pathname === subItem.path ? "gray" : "#1d1a57", fontSize: 13 }}>
                              {subItem.text}
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              )}
            </div>
          ))}
        </List>
      </Box>
      <Box sx={{ p: 2, borderTop: "1px solid #e0e0e0" }}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<LoginIcon />}
          onClick={() => {
            try {
              localStorage.setItem('ar_dashboard_logged_out', 'true');
            } catch (e) {}
            navigate('/');
          }}
          sx={{ color: "#C62828", borderColor: "#C62828", borderRadius: 1, "&:hover": { bgcolor: "#FFF5F5", borderColor: "#C12828" } }}
        >
          LOGOUT
        </Button>
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={isSidebarOpen}
        onClose={toggleSidebar}
        ModalProps={{ keepMounted: true }}
        sx={{
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            bgcolor: "#fff",
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  if (!isSidebarOpen) return <MiniDrawer />;

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <Drawer
        variant="permanent"
        sx={{
          width: isSmallScreen ? 225 : drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": { width: isSmallScreen ? 225 : drawerWidth, boxSizing: "border-box" },
        }}
      >
        {drawerContent}
      </Drawer>
    </Box>
  );
};

export default Sidebar;
